import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import PortfolioSummary from './components/PortfolioSummary';
import AssetAllocation from './components/AssetAllocation';
import PerformanceChart from './components/PerformanceChart';
import QuickActions from './components/QuickActions';
import RecentTransactions from './components/RecentTransactions';
import PriceAlerts from './components/PriceAlerts';
import SecurityStatus from './components/SecurityStatus';
import MarketOverview from './components/MarketOverview';
import transactionService from '../../services/transactionService';
import priceAlertService from '../../services/priceAlertService';
import portfolioService from '../../services/portfolioService';
import { useAuth } from '../../contexts/AuthContext';

const AccountDashboard = () => {
  const { user } = useAuth();
  const [portfolioData, setPortfolioData] = useState({
    totalValue: 0,
    change24h: 0,
    changePercent: 0,
    usdBalance: 0,
    cryptoBalance: 0
  });

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetAllocation, setAssetAllocation] = useState([]);
  const [performanceData] = useState([
  { date: '01/05', value: 42500 },
  { date: '01/06', value: 43200 },
  { date: '01/07', value: 42800 },
  { date: '01/08', value: 44100 },
  { date: '01/09', value: 45300 },
  { date: '01/10', value: 46200 },
  { date: '01/11', value: 47100 },
  { date: '01/12', value: 47856 }]
  );

  const [securityStatus] = useState({
    score: 75,
    features: [
    {
      id: 1,
      name: 'Two-Factor Authentication',
      description: 'SMS and authenticator app enabled',
      enabled: true
    },
    {
      id: 2,
      name: 'Email Verification',
      description: 'Verified email address',
      enabled: true
    },
    {
      id: 3,
      name: 'Biometric Login',
      description: 'Fingerprint or face recognition',
      enabled: false
    },
    {
      id: 4,
      name: 'Withdrawal Whitelist',
      description: 'Approved addresses only',
      enabled: true
    },
    {
      id: 5,
      name: 'Device Management',
      description: 'Trusted devices configured',
      enabled: false
    }]

  });

  const [marketOverview] = useState([
  {
    id: 1,
    name: 'Bitcoin',
    symbol: 'BTC',
    price: 58742.50,
    change24h: 3.45,
    icon: "https://images.unsplash.com/photo-1631865382441-b00105810564",
    iconAlt: 'Golden Bitcoin cryptocurrency coin with B symbol on dark background representing digital currency'
  },
  {
    id: 2,
    name: 'Ethereum',
    symbol: 'ETH',
    price: 2456.80,
    change24h: 2.18,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_117ea879d-1767158662331.png",
    iconAlt: 'Silver Ethereum cryptocurrency coin with diamond logo on blue gradient background'
  },
  {
    id: 3,
    name: 'Binance Coin',
    symbol: 'BNB',
    price: 312.45,
    change24h: -1.23,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1e895eb91-1764668859855.png",
    iconAlt: 'Yellow and black Binance cryptocurrency coin with BNB logo on gradient background'
  },
  {
    id: 4,
    name: 'Solana',
    symbol: 'SOL',
    price: 94.32,
    change24h: 5.67,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2cc2138-1764643920948.png",
    iconAlt: 'Purple and blue Solana cryptocurrency coin with gradient design on dark surface'
  },
  {
    id: 5,
    name: 'Cardano',
    symbol: 'ADA',
    price: 0.48,
    change24h: -0.89,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9cb1bb0-1764668859907.png",
    iconAlt: 'Blue Cardano cryptocurrency coin with geometric logo design on dark background'
  }]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch real transactions from database
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user) return;

      setLoading(true);
      const { data, error } = await transactionService?.getUserTransactions(user?.id, 5);

      if (data && !error) {
        // Transform database data to component format
        const transformedTransactions = data?.map((tx) => ({
          id: tx?.id,
          type: tx?.type,
          description: tx?.description,
          date: new Date(tx?.created_at)?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
          time: new Date(tx?.created_at)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          amount: tx?.usd_amount,
          cryptoAmount: tx?.crypto_amount ? `${tx?.crypto_amount} ${tx?.crypto_symbol}` : null,
          cryptoIcon: null,
          cryptoIconAlt: tx?.crypto_symbol ? `${tx?.crypto_symbol} cryptocurrency coin` : null
        }));
        setRecentTransactions(transformedTransactions);
      }
      setLoading(false);
    };

    fetchTransactions();
  }, [user]);

  // Fetch portfolio balance from actual transactions
  useEffect(() => {
    const fetchPortfolioBalance = async () => {
      if (!user) return;

      const { data, error } = await portfolioService?.getPortfolioBalance(user?.id);

      if (data && !error) {
        setPortfolioData(data);
      }
    };

    fetchPortfolioBalance();
  }, [user]);

  // Fetch asset allocation from actual transactions
  useEffect(() => {
    const fetchAssetAllocation = async () => {
      if (!user) return;

      const { data, error } = await portfolioService?.getAssetAllocation(user?.id);

      if (data && !error) {
        setAssetAllocation(data);
      }
    };

    fetchAssetAllocation();
  }, [user]);

  // Fetch real price alerts from database
  useEffect(() => {
    const fetchPriceAlerts = async () => {
      if (!user) return;

      const { data, error } = await priceAlertService?.getUserPriceAlerts(user?.id);

      if (data && !error) {
        // Transform database data to component format
        const transformedAlerts = data?.map((alert) => ({
          id: alert?.id,
          crypto: alert?.crypto_name,
          condition: alert?.condition,
          targetPrice: alert?.target_price,
          active: alert?.status === 'active',
          cryptoIcon: null,
          cryptoIconAlt: `${alert?.crypto_symbol} cryptocurrency coin`
        }));
        setPriceAlerts(transformedAlerts);
      }
    };

    fetchPriceAlerts();
  }, [user]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2">Account Dashboard</h1>
          <p className="text-sm md:text-base text-muted-foreground">Monitor your portfolio and manage your crypto assets</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="lg:col-span-2">
            <PortfolioSummary
              totalBalance={portfolioData?.totalValue}
              totalGain={portfolioData?.change24h}
              totalGainPercentage={portfolioData?.changePercent}
              cryptoBalance={portfolioData?.cryptoBalance}
              usdBalance={portfolioData?.usdBalance}
              data={portfolioData} />

          </div>
          <div>
            <SecurityStatus
              securityScore={securityStatus?.score}
              features={securityStatus?.features} />

          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <PerformanceChart data={performanceData} />
          <AssetAllocation assets={assetAllocation} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="lg:col-span-2">
            {loading ?
            <div className="bg-card rounded-xl p-6 md:p-8 shadow-md border border-border text-center">
                <p className="text-muted-foreground">Loading transactions...</p>
              </div> :

            <RecentTransactions transactions={recentTransactions} />
            }
          </div>
          <div>
            <PriceAlerts alerts={priceAlerts} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
          <MarketOverview topCryptos={marketOverview} />
          <QuickActions />
        </div>
      </div>
    </div>);

};

export default AccountDashboard;